/**
 * Curated, friendly property lists per game component type. The simple inspector shows only these;
 * edits still go to the same Unity serialized paths (prefab overrides / component data).
 */

export type FieldKind =
  /** number; shown as raw * scale */
  | { k: 'num'; step?: number; min?: number; max?: number; scale?: number; unit?: string }
  | { k: 'int'; min?: number; max?: number }
  | { k: 'bool'; invert?: boolean }
  | { k: 'enum'; options: [number, string][] }
  | { k: 'text'; placeholder?: string }
  /** FrameCounter ({_seconds, _frames}) edited in seconds */
  | { k: 'seconds' }
  | { k: 'color' }
  | { k: 'vec2'; step?: number }
  /** single ActivatorBase reference */
  | { k: 'activator' }
  /** List<ActivatorBase> */
  | { k: 'activators' }
  /** List<BodyControlPanel> */
  | { k: 'panels' }
  /** RigidBodyRotator.rotatePoints */
  | { k: 'rotatePoints' }
  /** BodyControlHandler.uiRigidBodiesOrGameObjects */
  | { k: 'controlled' }
  /** Serializable nullable (SN<T>): checkbox is hasValue, the control edits v. def is the prefab value shown while off. */
  | { k: 'opt'; of: 'bool' | 'num' | 'int'; step?: number; min?: number; max?: number };

export interface FieldSpec {
  p: string;
  label: string;
  hint?: string;
  kind: FieldKind;
  /** value used when the component data lacks the field */
  def?: string;
  /** section label, shown above the first field of a group */
  group?: string;
}

export interface CompSchema {
  title: string;
  fields: FieldSpec[];
}

export const FPS = 120;

const SOUNDS = ['None', 'Jump', 'WallJump', 'Coin', 'Dash', 'Jetpack', 'Checkpoint', 'PlayerDeath', 'UIButton', 'Powerup', 'Cannon'];

const e = (...names: string[]): [number, string][] => names.map((n, i) => [i, n]);
const num = (step = 0.1, extra: Partial<Extract<FieldKind, { k: 'num' }>> = {}): FieldKind => ({ k: 'num', step, ...extra });
const bool: FieldKind = { k: 'bool' };
const optBool: FieldKind = { k: 'opt', of: 'bool' };
const optNum = (step = 0.1, extra: { min?: number; max?: number } = {}): FieldKind => ({ k: 'opt', of: 'num', step, ...extra });
const optInt = (min?: number): FieldKind => ({ k: 'opt', of: 'int', min });
const secs: FieldKind = { k: 'seconds' };
const activator: FieldKind = { k: 'activator' };

const SWITCH_TYPE: FieldSpec = { p: 'switchType', label: 'Behaviour', kind: { k: 'enum', options: e('On while touched', 'Toggle on each touch', 'One time: stays on', 'One time: stays off') } };
const INVERT: FieldSpec = { p: 'invert', label: 'Inverted', hint: 'Reports on when it is off and off when it is on', kind: bool };
export const ACTIVATION_TYPES = e('All are on', 'Any is on', 'All on, then until all off');

export const SCHEMAS: Record<string, CompSchema> = {
  SwitchHandler: { title: 'Switch', fields: [SWITCH_TYPE, INVERT] },
  ZoneActivator: {
    title: 'Trigger zone',
    fields: [
      SWITCH_TYPE,
      INVERT,
      { p: 'onlyPlayerActivates', label: 'Only players trigger it', kind: bool, def: '1' },
      { p: 'requiredDashDirection', label: 'Needs a dash', kind: { k: 'enum', options: e('No', 'Any direction', 'Up', 'Up right', 'Right', 'Down right', 'Down', 'Down left', 'Left', 'Up left') } },
      { p: 'diagonalDashCountsForCardinal', label: 'Diagonal dashes count', kind: bool },
    ],
  },
  ActivatorModifier: {
    title: 'Logic gate',
    fields: [
      { p: 'activators', label: 'Inputs', kind: { k: 'activators' } },
      { p: 'activationType', label: 'On when inputs', kind: { k: 'enum', options: ACTIVATION_TYPES } },
      { p: 'invert', label: 'Invert output', kind: bool },
      { p: 'toggleOnActivate', label: 'Toggle on each activation', kind: bool },
      { p: 'automaticallyIncludeChildActivators', label: 'Include child activators', kind: bool, def: '1' },
    ],
  },
  TimedActivator: {
    title: 'Timer',
    fields: [
      { p: 'timerSpeed', label: 'Speed', kind: { k: 'enum', options: e('Very slow', 'Slow', 'Regular', 'Fast', 'Very fast') }, def: '2' },
      { p: 'customTimerInterval', label: 'Custom intervals (s)', hint: 'Comma separated seconds, e.g. 1, 0.5. Overrides the speed', kind: { k: 'text', placeholder: 'e.g. 1, 0.5' } },
      INVERT,
    ],
  },
  DashActivator: { title: 'Dash activator', fields: [{ p: 'toggle', label: 'Toggle on each dash', kind: bool }, { p: 'coolDownCounter', label: 'Cooldown', kind: secs }] },
  GemShardGroup: {
    title: 'Star coin group',
    fields: [
      { p: 'activator', label: 'Appears when', hint: 'Empty: appears right away', kind: activator },
      { p: 'canBeDeactivated', label: 'Disappears when turned off', kind: bool, def: '1' },
      { p: 'lifetimeCounter', label: 'Time limit', kind: secs },
    ],
  },
  PowerupCollectable: { title: 'Powerup', fields: [{ p: 'powerupType', label: 'Gives', kind: { k: 'enum', options: e('Nothing', 'Block Dude', 'Switch places', 'Double dash', 'Big Dude', 'Jetpack') } }] },
  ReDash: { title: 'Dash refill', fields: [{ p: 'respawnTime', label: 'Respawns after', kind: secs }] },
  ForceDash: { title: 'Force dash', fields: [{ p: 'respawnTime', label: 'Respawns after', kind: secs }, { p: 'fadeInTime', label: 'Fade in', kind: secs }] },
  RePack: {
    title: 'Jetpack refill',
    fields: [
      { p: 'respawnTime', label: 'Respawns after', kind: secs },
      { p: 'fadeInTime', label: 'Fade in', kind: secs },
      { p: 'jetpackRefillPercentage', label: 'Refill amount', kind: num(5, { scale: 100, unit: '%', min: 0, max: 100 }), def: '1' },
    ],
  },
  BodyControlHandler: {
    title: 'Body controller',
    fields: [
      { p: 'uiRigidBodiesOrGameObjects', label: 'Controlled bodies', kind: { k: 'controlled' } },
      { p: 'uiControlPanels', label: 'Control panels', hint: 'Players stand at a panel and press Use to steer', kind: { k: 'panels' } },
      { p: 'controlSpeed', label: 'Speed (tiles/s)', kind: num(0.5, { scale: FPS, min: 0 }), def: '0.2' },
      { p: 'controlAcceleration', label: 'Acceleration', kind: num(0.0005, { min: 0 }), def: '0.001' },
      { p: 'leftControlStart', label: 'Stick start', hint: 'Starting position of the left stick inside the range', kind: { k: 'vec2', step: 1 } },
      { p: 'xMin', label: 'Range X min', kind: num(1), def: '-1' },
      { p: 'xMax', label: 'Range X max', kind: num(1), def: '1' },
      { p: 'yMin', label: 'Range Y min', kind: num(1), def: '-1' },
      { p: 'yMax', label: 'Range Y max', kind: num(1), def: '1' },
      { p: 'rightControlStart', label: 'Right stick start', kind: { k: 'vec2', step: 1 } },
    ],
  },
  RigidBodyRotator: {
    title: 'Rotation',
    fields: [
      { p: 'activator', label: 'Rotates while', hint: 'Empty: always rotates', kind: activator },
      { p: 'activatorPauseType', label: 'When turned off', kind: { k: 'enum', options: e('Stop immediately', 'Finish the current turn') } },
      { p: 'masterRotateSpeed', label: 'Speed (°/s)', kind: num(30, { scale: FPS, min: 0 }) },
      { p: 'masterAcceleration', label: 'Acceleration (°/s²)', kind: num(100, { scale: FPS * FPS, min: 0 }) },
      { p: 'rotateDirection', label: 'Spin direction', hint: 'Only used without turn angles', kind: { k: 'enum', options: [[1, 'Counter-clockwise'], [-1, 'Clockwise']] }, def: '1' },
      { p: 'rotatePoints', label: 'Turn angles', kind: { k: 'rotatePoints' } },
      { p: 'startRotateIndex', label: 'Start at angle #', kind: { k: 'int', min: 0 } },
    ],
  },
  CameraPreviewHandler: {
    title: 'Camera preview',
    fields: [
      { p: 'activator', label: 'Starts when', kind: activator },
      { p: 'lookAtPosition', label: 'Look at', kind: { k: 'vec2', step: 1 } },
      { p: 'pauseAtDestinationCounter', label: 'Hold for', kind: secs },
      { p: 'moveSpeed', label: 'Camera speed', kind: { k: 'enum', options: e('Slow', 'Regular', 'Fast') } },
    ],
  },
  DynamicObject: {
    title: 'Respawning',
    fields: [
      { p: 'canDie', label: 'Can be destroyed', kind: bool, def: '1' },
      { p: 'canRespawn', label: 'Respawns', kind: bool, def: '1' },
      { p: 'startDespawned', label: 'Starts hidden', kind: bool },
      { p: 'killActivator', label: 'Destroyed when', kind: activator },
      { p: 'respawnActivator', label: 'Respawned when', kind: activator },
      { p: 'activatorForcesRespawn', label: 'Respawn even if alive', kind: bool },
    ],
  },
  DespawnTimer: {
    title: 'Despawn',
    fields: [
      { p: 'despawnTimer', label: 'Despawns after', kind: secs },
      { p: 'destroyOnDespawn', label: 'Destroy on despawn', kind: bool },
      { p: 'disableColliderOnFade', label: 'No collision while fading', kind: bool, def: '1' },
    ],
  },
  LaserHandler: {
    title: 'Laser',
    fields: [
      { p: 'activators', label: 'On while', hint: 'Empty: always on', kind: { k: 'activators' } },
      { p: 'activationType', label: 'On when activators', kind: { k: 'enum', options: e('All are on', 'Any is on') } },
      { p: 'color1', label: 'Colour 1', kind: { k: 'color' } },
      { p: 'color2', label: 'Colour 2', kind: { k: 'color' } },
      { p: 'lerpDuration', label: 'Pulse time (s)', kind: num(0.1, { min: 0.01 }), def: '0.4' },
    ],
  },
  ProjectileEmitter: {
    title: 'Cannon',
    fields: [
      { p: 'fireCounter', label: 'Fire every', kind: secs },
      { p: 'delayCounterBy', label: 'Start delay', kind: secs },
      { p: 'projectileSpeed', label: 'Projectile speed', kind: num(0.5, { min: 0 }), def: '8' },
      { p: 'projectileRotationSpeed', label: 'Projectile spin', kind: num(0.5), def: '1' },
      { p: 'activator', label: 'Fires while', hint: 'Empty: always fires', kind: activator },
      { p: 'singleFirePerActivate', label: 'Fire once per activation', kind: bool },
      { p: 'projectileInheritsEmitterVelocity', label: 'Shots inherit motion', kind: bool },
      { p: 'fireSound', label: 'Sound', kind: { k: 'enum', options: e(...SOUNDS) } },
      { p: 'overrideProjectileDespawnVars', label: 'Custom shot lifetime', kind: bool },
      { p: 'despawnTimer', label: 'Shot lifetime', kind: secs },
    ],
  },
  SpikeyPatroller: {
    title: 'Patroller',
    fields: [
      { p: 'speed', label: 'Speed', kind: num(0.5, { min: 0 }), def: '2' },
      { p: 'jumpVelocity', label: 'Jump strength', kind: num(1, { min: 0 }), def: '12' },
      { p: 'facingRight', label: 'Starts facing right', kind: bool, def: '1' },
      { p: 'jumpsOnPlayerJump', label: 'Jumps when players jump', kind: bool, def: '1' },
      { p: 'enableCliffSensing', label: 'Turns at ledges', kind: bool, def: '1' },
      { p: 'sightDistance', label: 'Sight distance', kind: num(1, { min: 0 }), def: '20' },
      { p: 'sightAngle', label: 'Sight angle (°)', kind: num(5, { min: 0, max: 360 }), def: '120' },
      { p: 'useLeftBoundary', label: 'Left limit', kind: bool },
      { p: 'leftBoundaryPosition', label: 'Left limit X', kind: num(1) },
      { p: 'useRightBoundary', label: 'Right limit', kind: bool },
      { p: 'rightBoundaryPosition', label: 'Right limit X', kind: num(1) },
    ],
  },
  LauncherBase: {
    title: 'Launcher',
    fields: [
      { p: 'activator', label: 'Launches when', kind: activator },
      { p: 'launcherMaxSpeed', label: 'Max speed', kind: num(0.05, { min: 0 }), def: '0.5' },
      { p: 'launcherAcceleration', label: 'Acceleration', kind: num(0.01, { min: 0 }), def: '0.1' },
      { p: 'makeActivatorFullWidth', label: 'Full-width trigger', kind: bool },
    ],
  },
  DeathTriggerHandler: { title: 'Hazard', fields: [{ p: 'onlyEffectPlayers', label: 'Only hurts players', kind: bool, def: '1' }] },
  LevelData: {
    title: 'Player string',
    fields: [
      { p: 'connectPlayersWithString', label: 'Tie players together', hint: 'A string links each player to the next. They die if it stretches past the max distance. Turn this off to remove the string.', kind: bool },
      { p: 'maxStringLength', label: 'Max distance', hint: 'How far apart linked players can get, in tiles', kind: num(1, { min: 0.5 }), def: '8' },
    ],
  },
  PlayerSpawnPoint: { title: 'Spawn', fields: [{ p: 'spawnOrder', label: 'Spawn order', hint: '0 = first player', kind: { k: 'int', min: 0 } }] },
  Checkpoint: {
    title: 'Checkpoint',
    fields: [
      { p: 'checkpointNumber', label: 'Number', hint: 'Checkpoints activate in this order', kind: { k: 'int' } },
      { p: 'playerDoesntTrigger', label: 'Not triggered by players', kind: bool },
    ],
  },
  Oscillator: {
    title: 'Bobbing',
    fields: [
      { p: 'amplitude', label: 'Height', kind: num(0.01, { min: 0 }), def: '0.055' },
      { p: 'frequency', label: 'Speed', kind: num(0.1, { min: 0 }), def: '0.7' },
      { p: 'offset', label: 'Phase offset', kind: num(0.1) },
    ],
  },
  TouchBlock: { title: 'Touch block', fields: [{ p: 'fadeToColor', label: 'Touched colour', kind: { k: 'color' } }] },
  TickTockPlatformHandler: {
    title: 'Tick-tock platform',
    fields: [
      { p: 'activator', label: 'Toggled by', kind: activator },
      { p: 'invert', label: 'Inverted', kind: bool },
      { p: 'primaryColor', label: 'Colour', kind: { k: 'color' } },
      { p: 'invertColor', label: 'Inverted colour', kind: { k: 'color' } },
    ],
  },
  DashPlatform: { title: 'Dash platform', fields: [{ p: 'staySolidCounter', label: 'Stays solid for', kind: secs }] },
  ColorMatchBlock: {
    title: 'Colour match block',
    fields: [
      { p: 'playerIndex', label: 'Solid for', kind: { k: 'enum', options: e('Player 1', 'Player 2', 'Player 3', 'Player 4') } },
      { p: 'backToSolidCounter', label: 'Back to solid after', kind: secs },
    ],
  },
  CrumblePlatformGenerator: { title: 'Crumble platform', fields: [{ p: 'roundLeftSide', label: 'Round left end', kind: bool }, { p: 'roundRightSide', label: 'Round right end', kind: bool }] },
  FadeOutHandler: { title: 'Fade away', fields: [{ p: 'activator', label: 'Fades away when', hint: 'Without an activator it never fades', kind: activator }] },
  PlayerOverrides: {
    title: 'Player',
    fields: [
      { p: 'allowJump', label: 'Can jump', group: 'Abilities', kind: optBool, def: '1' },
      { p: 'allowDash', label: 'Can dash', kind: optBool, def: '1' },
      { p: 'allowDashLaunching', label: 'Dash launches players', hint: 'Hitting another player during a dash knocks them', kind: optBool, def: '1' },
      { p: 'dashLaunchMultiplier', label: 'Launch multiplier', hint: 'Dash speed multiplied by this after hitting a player', kind: optNum(0.01, { min: 0 }), def: '0.66' },
      { p: 'dashCount', label: 'Dashes', hint: 'Dashes before the player has to touch the ground', kind: optInt(0), def: '1' },
      { p: 'allowWallJump', label: 'Can wall jump', kind: optBool, def: '1' },
      { p: 'allowWallSlide', label: 'Can wall slide', kind: optBool, def: '1' },
      { p: 'allowControl', label: 'Can move', hint: 'Left stick and the keyboard move the player', kind: optBool, def: '1' },
      { p: 'killOnPressRestart', label: 'Restart kills', hint: 'Select / restart kills the player', kind: optBool, def: '1' },
      { p: 'maxMovementSpeed', label: 'Run speed', group: 'Movement', kind: optNum(0.1), def: '7' },
      { p: 'jumpVelocity', label: 'Jump speed', kind: optNum(0.1), def: '11' },
      { p: 'wallJumpXVelocity', label: 'Wall jump sideways', kind: optNum(0.1), def: '10' },
      { p: 'wallJumpYVelocity', label: 'Wall jump up', kind: optNum(0.1), def: '4' },
      { p: 'dashVelocity', label: 'Dash speed', kind: optNum(0.5), def: '25' },
      { p: 'postDashVelocity', label: 'Speed after a dash', kind: optNum(0.1), def: '9' },
      { p: 'wallSlideVelocity', label: 'Wall slide speed', kind: optNum(0.1), def: '-1.5' },
      { p: 'maxCoyoteCount', label: 'Coyote frames', group: 'Timing', hint: 'Frames off a ledge where a jump still works', kind: optInt(0), def: '7' },
      { p: 'dashFrames', label: 'Dash frames', kind: optInt(1), def: '20' },
      { p: 'allowDashAgainFrames', label: 'Frames between dashes', kind: optInt(0), def: '10' },
      { p: 'jumpHoldFrames', label: 'Jump hold frames', hint: 'How long holding jump keeps adding height', kind: optInt(0), def: '15' },
      { p: 'wallSlideCounterMax', label: 'Wall slide frames', kind: optInt(0), def: '100000' },
      { p: 'postWallJumpCounterMax', label: 'Frames after a wall jump', hint: 'Movement toward the wall is ignored so you cannot climb', kind: optInt(0), def: '25' },
      { p: 'onlyAllow45DegreeDash', label: 'Dash on 45° angles', group: 'Other', kind: optBool, def: '1' },
      { p: 'jumpReleaseMultiplier', label: 'Jump release', hint: 'Upward speed multiplied by this when jump is released', kind: optNum(0.05, { min: 0 }), def: '0.6' },
      { p: 'maxGroundAngle', label: 'Max ground angle', hint: 'Steeper than this is not ground', kind: optNum(1), def: '47' },
      { p: 'maxWallAngle', label: 'Wall angle slack', hint: 'How far from vertical still counts as a wall', kind: optNum(0.1, { min: 0 }), def: '1' },
    ],
  },
};

/** A PlayerOverrides component with every field present and not applied (prefab defaults). */
export function emptyPlayerOverrides(): Record<string, unknown> {
  const data: Record<string, unknown> = { m_Enabled: '1' };
  for (const f of SCHEMAS.PlayerOverrides.fields) data[f.p] = { v: f.def ?? '0', hasValue: '0' };
  return data;
}

/** Fields holding scene references that can be linked on the canvas, in "primary link" priority order. */
export const LINK_KINDS = ['activators', 'activator', 'panels'] as const;

export function linkFields(type: string): FieldSpec[] {
  const s = SCHEMAS[type];
  if (!s) return [];
  return s.fields.filter((f) => f.kind.k === 'activator' || f.kind.k === 'activators' || f.kind.k === 'panels');
}
